import { BadArgumentException, CrmError } from "./error";
import { Maybe } from "./util/maybe";
/**
 * InternalId is intended to replace almost all uses of parseId(input).toUuid().toString()
 * There are several instances (especially client-side) of Record<string, T> where the index
 * might be an objectId, UUID, or who knows what. The code is then forced to access data by
 * using foo[parseId(myId).toUuid().toString()] to ensure the data comes back as expected.
 * Given the type InternalId, this record may now be defined as Record<InternalId, T> and it will
 * let accessors of the record not cast data if they already are confident the ID is represented
 * as a UUID.
 *
 * example:
 * const takesUuidString = (foo: InternalId) => {
 *     console.log(foo);
 * }
 *
 * const legacyId = parseId('5e922bc4ff8bafd78a6b3641');
 * takesUuidString(legacyId.toObjectId().toString()); // <-- will not compile!
 * takesUuidString(legacyId.toUuid().toString());
 */
export const nilUuidString = "00000000-0000-0000-0000-000000000000";
export class NullId {
    constructor() {
        this.isSurrogateId = true;
        this.kind = "null";
    }
    equals(_) {
        return false;
    }
    toString() {
        return "";
    }
    toObjectId() {
        return new ObjectId("000000000000000000000000");
    }
    toUuid() {
        return new UUID(nilUuidString);
    }
    get isNull() {
        return true;
    }
}
export const genUuid = () => new UUID(crypto.randomUUID());
export class InvalidObjectId extends CrmError {
    constructor() {
        super(...arguments);
        this.name = "InvalidObjectId";
        this.skipReporting = true;
        this.errorType = "INVALID_OBJECT_ID";
    }
}
export class ObjectId {
    constructor(id) {
        this.isSurrogateId = true;
        this.kind = "objectid";
        if (!id || id.length === 0) {
            throw new InvalidObjectId(`Attempted to create invalid ObjectId (${id} ${typeof id})`);
        }
        this.id = id.toString();
    }
    equals(otherId) {
        if (otherId.kind === "objectid") {
            return this.id === otherId.id;
        }
        return this.toUuid().toString() === otherId.toUuid().toString();
    }
    toObjectId() {
        return this;
    }
    toUuid() {
        const segments = [
            this.id.substring(0, 8),
            this.id.substring(8, 12),
            "5555",
            "bbbb",
            this.id.substring(12, 24),
        ];
        const rawUuid = segments.join("-");
        return new UUID(rawUuid);
    }
    toString() {
        return this.id;
    }
    get isNull() {
        return false;
    }
}
export class InvalidUUIDException extends CrmError {
    constructor() {
        super(...arguments);
        this.name = "InvalidUUIDException";
        this.skipReporting = true;
        this.errorType = "INVALID_UUID";
    }
}
const uuidRegex = /^[0-9a-fA-F]{8}\b-[0-9a-fA-F]{4}\b-[0-9a-fA-F]{4}\b-[0-9a-fA-F]{4}\b-[0-9a-fA-F]{12}$/gi;
export class UUID {
    constructor(id) {
        this.isSurrogateId = true;
        this.kind = "uuid";
        if (!id || id.length !== 36) {
            throw new InvalidUUIDException(`${id} is not a valid UUID`);
        }
        if (!id.match(uuidRegex)) {
            throw new InvalidUUIDException(`${id} is not a valid UUID`);
        }
        this.uuid = id.toLowerCase();
    }
    equals(otherId) {
        return this.uuid === otherId.toUuid().toString();
    }
    isEmpty() {
        return this.uuid === nilUuidString;
    }
    get isNull() {
        return this.isEmpty();
    }
    toUuid() {
        return this;
    }
    toObjectId() {
        if (this.isEmpty()) {
            return new NullId();
        }
        const segments = this.uuid.split("-");
        if (segments[2] !== "5555" || segments[3] !== "bbbb") {
            throw new BadArgumentException(`Can't cast ${this.uuid} to ObjectId.`);
        }
        return new ObjectId([segments[0], segments[1], segments[4]].join(""));
    }
    toString() {
        return this.uuid;
    }
}
const parseId = (unparsedId) => {
    if (!unparsedId)
        return new NullId();
    if (unparsedId.isSurrogateId) {
        return unparsedId;
    }
    const id = unparsedId;
    if (id.length >= 36)
        return new UUID(id);
    return new ObjectId(id);
};
export const genInternalId = () => genUuid().toUuid().toString();
export function genEntityId() {
    return genUuid().toUuid().toString();
}
export function nullEntityId() {
    return parseEntityId(nilUuidString);
}
export function isNonNilEntityId(id) {
    return !!id && id !== nilUuidString;
}
export const parseInternalId = (unparsedId) => parseId(unparsedId).toUuid().toString();
export function parseEntityId(unparsedId) {
    return parseId(unparsedId).toUuid().toString();
}
export function parseEntityIdSafe(unparsedId) {
    try {
        return Maybe.fromValue(parseEntityId(unparsedId));
    }
    catch {
        return Maybe.none();
    }
}
/**
 *
 * @deprecated Needed for backwards compatibility. Do not use anywhere new.
 */
export function parseObjectIdSafe(unparsedId) {
    try {
        return Maybe.fromValue(parseId(unparsedId).toObjectId().toString());
    }
    catch {
        return Maybe.none();
    }
}
//# sourceMappingURL=id-parser.js.map